import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import {
  ApiError,
  listPlaySessions,
  type PlaySessionView,
} from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

/**
 * Dashboard panel: "現在玩樂中" — 玩樂實際使用視角 (complementary to
 * reservation view). Shows a compact list of active sessions with
 * party size + elapsed time + rolling estimate.
 *
 * Reuses listPlaySessions from api-client; refreshes every 30s.
 * Zero backend-side addition — pure read-only composition of existing
 * data. Follows dashboard pattern (lean Paper + subheader).
 */

export function NowPlayingPanel({ venueId }: { venueId: string }): JSX.Element {
  const [sessions, setSessions] = useState<readonly PlaySessionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listPlaySessions(venueId);
      setSessions(r.items);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    void refresh();
    const iv = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(iv);
  }, [refresh]);

  const totals = useMemo(() => {
    const totalPeople = sessions.reduce((n, s) => n + s.partySize, 0);
    const totalEstimate = sessions.reduce((n, s) => n + (s.currentEstimate ?? 0), 0);
    return { totalPeople, totalEstimate };
  }, [sessions]);

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="sm">
        <Title order={5}>
          <IconPlayerPlay
            size={16}
            color={tokens.color.primary}
            style={{ verticalAlign: 'middle', marginRight: 6 }}
          />
          現在玩樂中
        </Title>
        <Badge variant="light" color={sessions.length > 0 ? 'green' : 'gray'}>
          {sessions.length} 組 · {totals.totalPeople} 人
        </Badge>
      </Group>

      {loading && <Text c="dimmed" size="sm">讀取中…</Text>}
      {err && <Text c="red" size="sm">{err}</Text>}

      {!loading && sessions.length === 0 && !err && (
        <Text c="dimmed" size="sm" ta="center" py="sm">
          目前沒有進行中的玩樂時段
        </Text>
      )}

      {sessions.length > 0 && (
        <Stack gap="xs">
          {sessions.map((s) => (
            <SessionRow key={s.sessionId} session={s} />
          ))}
          <Text size="xs" c="dimmed" ta="right" mt={4}>
            預估總收 · NT$ {totals.totalEstimate.toLocaleString()}
          </Text>
        </Stack>
      )}
    </Paper>
  );
}

function SessionRow({ session }: { session: PlaySessionView }): JSX.Element {
  const startMs = new Date(session.startedAtIso).getTime();
  const elapsedMin = Math.max(0, Math.floor((Date.now() - startMs) / 60_000));
  const hrs = Math.floor(elapsedMin / 60);
  const mins = elapsedMin % 60;
  const elapsedLabel = hrs > 0 ? `${hrs}hr ${mins}min` : `${mins}min`;
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Stack gap={0}>
        <Text size="sm" fw={500}>
          {session.zoneId}
        </Text>
        <Text size="xs" c="dimmed">
          {session.partySize} 人 · 已坐 {elapsedLabel}
          {session.reservationId && ' · 訂位'}
        </Text>
      </Stack>
      {session.currentEstimate !== null && (
        <Text size="sm" c={tokens.color.primary} fw={600}>
          NT$ {session.currentEstimate}
        </Text>
      )}
    </Group>
  );
}
